/**
 * Convert flat error field paths to nested error objects for Formik
 *
 * Example:
 * Input: [{field: "LocalBankTransfer.EUR.IBAN", message: "Invalid IBAN"}]
 * Output: {LocalBankTransfer: {EUR: {IBAN: "Invalid IBAN"}}}
 */
export function transformFlatErrorsToNested(
  errors: Array<{ field: string; message: string }>
): Record<string, any> {
  const nestedErrors: Record<string, any> = {};

  errors.forEach((error) => {
    const parts = error.field.split('.');
    let current = nestedErrors;

    // Navigate/create the nested structure
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current[part]) {
        current[part] = {};
      }
      current = current[part];
    }

    // Set the error message at the final level
    const lastPart = parts[parts.length - 1];
    current[lastPart] = error.message;
  });

  return nestedErrors;
}

/**
 * Transform validation error response to have nested error structure
 */
export function transformValidationError(error: {
  errors?: Array<{ field: string; message: string }>;
  [key: string]: any;
}): any {
  if (error?.errors && Array.isArray(error.errors)) {
    // Create a new error object with transformed errors
    const transformedError = {
      ...error,
      errors: error.errors, // Keep original for debugging
      nestedErrors: transformFlatErrorsToNested(error.errors),
    };
    return transformedError;
  }
  return error;
}

/**
 * Filter errors to only include errors for fields that have been touched by the user
 * This prevents showing stale validation errors for untouched fields
 *
 * @param errors - Nested error object from Formik
 * @param touched - Nested touched object from Formik
 * @returns Filtered error object containing only errors for touched fields
 */
export function filterErrorsByTouched(
  errors: Record<string, any>,
  touched: Record<string, any>
): Record<string, any> {
  const filtered: Record<string, any> = {};

  const walkErrors = (errorObj: any, touchedObj: any, targetObj: any) => {
    if (!errorObj || typeof errorObj !== 'object') {
      return;
    }

    Object.entries(errorObj).forEach(([key, value]) => {
      if (typeof value === 'string') {
        // Leaf error - check if field is touched
        if (touchedObj && touchedObj[key]) {
          targetObj[key] = value;
        }
      } else if (typeof value === 'object' && value !== null) {
        // Nested errors - check if nested object has any touched fields
        const nestedTouched = touchedObj && touchedObj[key];
        if (nestedTouched && typeof nestedTouched === 'object') {
          const nestedTarget: any = {};
          walkErrors(value, nestedTouched, nestedTarget);

          // Only add nested object if it has any errors
          if (Object.keys(nestedTarget).length > 0) {
            targetObj[key] = nestedTarget;
          }
        }
      }
    });
  };

  walkErrors(errors, touched, filtered);
  return filtered;
}
